import type { CellClassParams } from 'ag-grid-community';

import { getSizeFromDensity, TableCellRenderer } from './table-format-helpers';
import { ChartTypes } from '@mode/shared/contract-common';

/**
 * Rule function factory for custom user conditional formatting.
 */
export function RulesFunctionFactory(rules: any) {
  return (params: CellClassParams): { [key: string]: any } | undefined => {
    for (let i = 0; i < rules.length; i++) {
      switch (rules[i].type) {
        case '===':
          if (params.value === rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
        case '!==':
          if (params.value !== rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
        case '>':
          if (params.value > rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
        case '<':
          if (params.value < rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
        case '>=':
          if (params.value >= rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
        case '<=':
          if (params.value <= rules[i].value) {
            return { backgroundColor: rules[i].color, color: rules[i].textColor };
          }
          break;
      }
    }

    return undefined;
  };
}

/**
 * Hex to RGB helper.
 */
export function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Get a legible text color based on the bg color passed.
 */
export function getTextColor(bgHexColor: string): string {
  const rgb = hexToRgb(bgHexColor);
  const brightness = rgb ? Math.round((rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000) : 200;

  if (brightness > 125) {
    return '#393945';
  } else {
    return '#c9cad1';
  }
}

/**
 * Validate by table settings.
 */
export function validateSettingsByTable(settings: ChartTypes.ByTableSettings): ChartTypes.CustomSettingsValidation {
  const isValid =
    Array.isArray(settings.columnNames) &&
    settings.columnNames.filter((colName) => typeof colName !== 'string').length === 0 &&
    Array.isArray(settings.rules);

  if (!isValid) {
    return {
      isValid: false,
      error: 'The `formatByTable` conditional styles configuration has a syntax error.',
    };
  }

  for (let i = 0; i < settings.rules.length; i++) {
    const isHex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(settings.rules[i].color);
    if (!isHex) {
      return {
        isValid: false,
        error: 'Colors must be a valid hex value.',
      };
    }
  }

  return { isValid: true };
}

/**
 * Validate by column settings.
 */
export function validateSettingsByColumn(settings: ChartTypes.ByColumnSettings): ChartTypes.CustomSettingsValidation {
  const isValid =
    Array.isArray(settings.columns) &&
    settings.columns.filter((column) => typeof column.name !== 'string' || !Array.isArray(column.rules)).length === 0;

  if (!isValid) {
    return {
      isValid: false,
      error: 'The `formatByColumn` conditional styles configuration has a syntax error.',
    };
  }

  for (let i = 0; i < settings.columns.length; i++) {
    for (let j = 0; j < settings.columns[i].rules.length; j++) {
      const isHex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(settings.columns[i].rules[j].color);
      if (!isHex) {
        return {
          isValid: false,
          error: 'Colors must be a valid hex value.',
        };
      }
    }
  }

  return { isValid: true };
}

/**
 * Process custom (by table) format settings.
 *
 * Generate a corresponding text color for a given bg color.
 */
export function processFormatSettingsByTable(settings: ChartTypes.ByTableSettings): ChartTypes.ByTableSettings {
  settings.rules.forEach((rule, i) => {
    settings.rules[i].textColor = getTextColor(rule.color);
  });

  return settings;
}

/**
 * Process custom (by column) format settings.
 *
 * Generate a corresponding text color for a given bg color.
 */
export function processFormatSettingsByColumn(settings: ChartTypes.ByColumnSettings): ChartTypes.ByColumnSettings {
  settings.columns.forEach((column, i) => {
    column.rules.forEach((rule, j) => {
      settings.columns[i].rules[j].textColor = getTextColor(rule.color);
    });
  });

  return settings;
}

/**
 * Valid `addImages` settings to try and prevent any component failure introduce by
 * custom user settings.
 */
export function validateImageSettings(settings: ChartTypes.AddImageSettings): ChartTypes.CustomSettingsValidation {
  const isValid =
    settings.columnNames.filter((columnName) => typeof columnName !== 'string').length === 0 &&
    ((typeof settings.rowHeightOverride === 'number' &&
      settings.rowHeightOverride > getSizeFromDensity(ChartTypes.FlatTableDensities.Compact)) ||
      !settings.rowHeightOverride);

  if (!isValid) {
    return {
      isValid: false,
      error: 'The `addImages` configuration has a syntax error.',
    };
  }

  return { isValid: true };
}

export function getCustomTableConfigMapFromUserSettingsArray(
  userDefinedSettingsArray: ChartTypes.FlatTableUserProvidedSettings[]
) {
  const stateMap: { [key: string]: ChartTypes.FlatTableCustomConfig } = {};
  for (let i = 0; i < userDefinedSettingsArray.length; i++) {
    const transformResult = getCustomTableConfigFromUserSettings(userDefinedSettingsArray[i]);
    if (transformResult) {
      stateMap[userDefinedSettingsArray[i].vizId] = transformResult;
    }
  }

  return stateMap;
}

/**
 * Transform custom user table settings to appropriate config.
 */
export function getCustomTableConfigFromUserSettings(
  userDefinedSettings: ChartTypes.FlatTableUserProvidedSettings
): ChartTypes.FlatTableCustomConfig | null {
  const validConfig: ChartTypes.FlatTableCustomConfig = {
    tableColStyleFunctions: {},
    tableColRenderers: {},
    tableSettings: {},
  };

  // loop through settings and validate, if valid, process and transform to config
  // validate
  if (typeof userDefinedSettings.vizId !== 'string') {
    logSettingsError(userDefinedSettings.vizId, 'A "tableId" must be present and must be a string.');
    return null;
  }
  if (userDefinedSettings.formatByTable) {
    const validation = validateSettingsByTable(userDefinedSettings.formatByTable);
    if (!validation.isValid) {
      logSettingsError(userDefinedSettings.vizId, validation.error ?? '');
      return null;
    }
  } else if (userDefinedSettings.formatByColumn) {
    const validation = validateSettingsByColumn(userDefinedSettings.formatByColumn);
    if (!validation.isValid) {
      logSettingsError(userDefinedSettings.vizId, validation.error ?? '');
      return null;
    }
  }

  if (userDefinedSettings.addImages) {
    const validation = validateImageSettings(userDefinedSettings.addImages);
    if (!validation.isValid) {
      logSettingsError(userDefinedSettings.vizId, validation.error ?? '');
      return null;
    }
  }

  // if valid, process and update validConfig object
  if (userDefinedSettings.formatByTable) {
    const processedSettings = processFormatSettingsByTable(userDefinedSettings.formatByTable);
    const ruleFunction = RulesFunctionFactory(processedSettings.rules);
    if (ruleFunction) {
      processedSettings.columnNames.forEach((colName) => {
        validConfig.tableColStyleFunctions[colName] = ruleFunction;
      });
    }
  } else if (userDefinedSettings.formatByColumn) {
    const processedSettings = processFormatSettingsByColumn(userDefinedSettings.formatByColumn);
    processedSettings.columns.forEach((column) => {
      const ruleFunction = RulesFunctionFactory(column.rules);
      if (ruleFunction) {
        validConfig.tableColStyleFunctions[column.name] = ruleFunction;
      }
    });
  }

  // Check for image columns, if present and valid, process the settings.
  if (userDefinedSettings.addImages) {
    // Add image renderers
    userDefinedSettings.addImages.columnNames.forEach((colName) => {
      validConfig.tableColRenderers[colName] = TableCellRenderer.Image;
    });

    // If present, add row height override
    if (userDefinedSettings.addImages.rowHeightOverride) {
      validConfig.tableSettings['rowHeight'] = Math.floor(userDefinedSettings.addImages.rowHeightOverride);
    }
  }

  return validConfig;
}

export function logSettingsError(vizId: string, error: string) {
  console.error(
    `Custom settings for visualization ID '${vizId}' are not valid and have failed with the following error: "${error}"`
  );
}
